import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ar";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { fetchNotifications } from "../../features/auth/userSlice";
import { AppDispatch, RootState } from "../../store";
import {  PaginationType } from "../../types";
import { Link } from "react-router-dom";

const Notifications = ({
    open,
    setOpen,
}: {
    open: boolean;
    setOpen: any;
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: RootState) => state.user);

    const [notifications, setNotifications] = useState<any[]>([]);
    const [pagination, setPagination] = useState<PaginationType | null>(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);

    const listRef = useRef<HTMLUListElement | null>(null);

    const loadNotifications = async (pageNum: number) => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const res = await dispatch(
                fetchNotifications({ userId: user.id, page: pageNum })
            ).unwrap();

            // المتوقع الـ API يرجع { data, pagination }
            if (pageNum === 1) {
                setNotifications(res.data);
            } else {
                setNotifications((prev) => [...prev, ...res.data]);
            }
            setPagination(res.pagination);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id) {
            setPage(1);
            loadNotifications(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;

        const handleScroll = () => {
            if (
                el.scrollTop + el.clientHeight >= el.scrollHeight - 50 &&
                !loading &&
                pagination &&
                page < pagination.lastPage
            ) {
                const nextPage = page + 1;
                setPage(nextPage);
                loadNotifications(nextPage);
            }
        };

        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pagination, loading]);

    const { t, i18n } = useTranslation();
    dayjs.extend(relativeTime);
    dayjs.locale(`${i18n.language}`);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="notifications"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col h-full"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between bg-primary/10 px-4 py-3 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-darkColor">
                            {t("notifications")}
                        </h3>
                        <button
                            onClick={() => setOpen(false)}
                            className="text-gray-500 hover:text-red-500 transition text-xl"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Notifications List */}
                    <ul ref={listRef} className="flex-1 overflow-y-auto">
                        <AnimatePresence>
                            {notifications?.map((item, index) => (
                                <motion.li
                                    key={index}
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 50 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    className=" border-b border-gray-100 hover:bg-gray-50 transition"
                                >
                                    <Link onClick={()=>setOpen(false)} className="px-4 flex gap-2 py-3" to={`me/book-details/${item?.data}`}>
                                    <div className="w-5 h-5 bg-gray-400 rounded-full ">
                                        <img className="w-full h-full " src={`${item?.action === "BOOK_HALL" ? "/asset/Avatar type m.png" : item?.action === "BOOK_STAY" ? "/asset/Avatar type n.png" : item.action === "CHANGE_WAITING_BOOK_STAY"||item.action === "CHANGE_WAITING_BOOK_HALL" ? "/asset/done.png" : "/asset/cancel.png"}`} alt="notification-image" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-800">{item?.title}</p>
                                        <p className="text-sm text-gray-800">{item?.body}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {dayjs(item?.createAt).fromNow()}
                                        </p>
                                    </div>
                                    </Link>
                                    

                                </motion.li>
                            ))}
                        </AnimatePresence>
                        {loading && (
                            <li className="text-center py-2 text-gray-400 text-sm">
                                {t("loading")}...
                            </li>
                        )}
                    </ul>
                    {(!notifications || notifications?.length === 0) && !loading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="p-4 text-center text-gray-500 text-sm"
                        >
                            {t("noNotifications")}
                        </motion.div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Notifications;
